// src/components/Profile/ProfileContainer.jsx
import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Box,
    Typography,
    Paper,
    Grid,
    Avatar,
    Button,
    Chip,
    Stack,
    Divider,
} from '@mui/material';
import { Edit as EditIcon, Person as PersonIcon } from '@mui/icons-material';
import { useGetProfile } from '../../hooks/profile/useGetProfile';
import { useAuthContext } from '../../auth/useAuthContext';
import LoadingPage from '../global/LoadingPage';
import WrapperSection from '../global/WrapperSection';
import ProfileEditForm from './ProfileEditForm';

const ProfileContainer = () => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { user } = useAuthContext();
    const { profile, isLoading, error, refetch } = useGetProfile();
    const [isEditMode, setIsEditMode] = useState(false);

    if (isLoading) return <LoadingPage />;

    if (error) {
        return (
            <Container maxWidth="md">
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="body1" color="error">
                        {isArabic
                            ? 'تعذر تحميل بيانات الملف الشخصي'
                            : 'Failed to load profile data'}
                    </Typography>
                </Box>
            </Container>
        );
    }

    const handleEditSuccess = () => {
        setIsEditMode(false);
        refetch();
    };

    return (
        <Box
            sx={{
                py: 4,
                px: 2,
                background: isDark
                    ? 'linear-gradient(135deg, #020617, #111827)'
                    : 'linear-gradient(135deg, #EEF2FF, #E0F2FE)',
                minHeight: 'calc(100vh - 64px)',
            }}
        >
            <WrapperSection>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        textAlign={isArabic ? 'right' : 'left'}
                        gutterBottom
                    >
                        {isArabic ? 'الملف الشخصي' : 'Profile'}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign={isArabic ? 'right' : 'left'}
                    >
                        {isArabic
                            ? 'يمكنك هنا استعراض وتعديل بيانات حسابك الشخصية.'
                            : 'You can view and edit your personal account information here.'}
                    </Typography>
                </Box>

                {/* Content */}
                <Grid container spacing={3}>
                    {/* Basic Info Card */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'white',
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 120,
                                    height: 120,
                                    mx: 'auto',
                                    mb: 2,
                                    bgcolor: theme.palette.primary.main,
                                    fontSize: '3rem',
                                }}
                            >
                                {profile?.first_name?.charAt(0)?.toUpperCase() ||
                                    user?.name?.charAt(0)?.toUpperCase() ||
                                    <PersonIcon fontSize="large" />}
                            </Avatar>

                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                {profile?.full_name || user?.name || '-'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {user?.email || '-'}
                            </Typography>

                            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                                <Chip
                                    label={user?.user_type === 'admin' ? (isArabic ? 'مدير' : 'Admin') : (isArabic ? 'عميل' : 'Customer')}
                                    color="primary"
                                    size="small"
                                />
                                {user?.is_active && (
                                    <Chip
                                        label={isArabic ? 'نشط' : 'Active'}
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Stack>

                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                fullWidth
                                sx={{ mt: 3 }}
                                onClick={() => setIsEditMode(!isEditMode)}
                            >
                                {isEditMode
                                    ? (isArabic ? 'إلغاء التعديل' : 'Cancel Edit')
                                    : (isArabic ? 'تعديل البيانات' : 'Edit Profile')}
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Details / Edit Form */}
                    <Grid item xs={12} md={8}>
                        {isEditMode ? (
                            <ProfileEditForm
                                currentData={profile}
                                isArabic={isArabic}
                                onSuccess={handleEditSuccess}
                                onCancel={() => setIsEditMode(false)}
                            />
                        ) : (
                            <Paper
                                sx={{
                                    p: 3,
                                    bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'white',
                                }}
                            >
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {isArabic ? 'المعلومات الشخصية' : 'Personal Information'}
                                </Typography>
                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <ProfileField
                                        label={isArabic ? 'الاسم الأول' : 'First Name'}
                                        value={profile?.first_name}
                                    />
                                    <ProfileField
                                        label={isArabic ? 'الاسم الأخير' : 'Last Name'}
                                        value={profile?.last_name}
                                    />
                                    <ProfileField
                                        label={isArabic ? 'الجنس' : 'Gender'}
                                        value={
                                            profile?.gender
                                                ? (isArabic
                                                    ? (profile.gender === 'male' ? 'ذكر' : 'أنثى')
                                                    : profile.gender)
                                                : '-'
                                        }
                                    />
                                    <ProfileField
                                        label={isArabic ? 'تاريخ الميلاد' : 'Birthdate'}
                                        value={
                                            profile?.birthdate
                                                ? new Date(profile.birthdate).toLocaleDateString(
                                                    isArabic ? 'ar-EG' : 'en-US',
                                                    { year: 'numeric', month: 'long', day: 'numeric' }
                                                )
                                                : '-'
                                        }
                                    />
                                    <ProfileField
                                        label={isArabic ? 'رقم الهاتف' : 'Phone'}
                                        value={user?.phone}
                                    />
                                    <ProfileField
                                        label={isArabic ? 'البريد الإلكتروني' : 'Email'}
                                        value={user?.email}
                                    />
                                    <ProfileField
                                        label={isArabic ? 'اللغة المفضلة' : 'Preferred Language'}
                                        value={user?.lang === 'ar' ? (isArabic ? 'العربية' : 'Arabic') : (isArabic ? 'الإنجليزية' : 'English')}
                                    />
                                    <ProfileField
                                        label={isArabic ? 'تاريخ الإنشاء' : 'Created At'}
                                        value={
                                            profile?.created_at
                                                ? new Date(profile.created_at).toLocaleDateString(
                                                    isArabic ? 'ar-EG' : 'en-US',
                                                    { year: 'numeric', month: 'short', day: 'numeric' }
                                                )
                                                : '-'
                                        }
                                    />
                                </Grid>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            </WrapperSection>
        </Box>
    );
};

const ProfileField = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="text.secondary" display="block">
            {label}
        </Typography>
        <Typography variant="body1" fontWeight={500}>
            {value || '-'}
        </Typography>
    </Grid>
);

export default ProfileContainer;
